import { AfterViewInit, Component, computed, effect, ElementRef, HostListener, inject, NgZone, signal, ViewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { DevBottomBarService } from '../dev-bottom-bar.service';
import { DevConsoleService } from './dev-console.service';
import { CONSOLE_LEVELS, ConsoleEntry, ConsoleLevel } from './dev-console.types';
import { JsonViewerComponent } from '../json-viewer/json-viewer.component';
import { ComponentInspectorPanelComponent } from '../component-inspector/component-inspector-panel.component';

@Component({
  selector: 'app-dev-console-panel',
  standalone: true,
  imports: [NgTemplateOutlet, MatIconModule, JsonViewerComponent, ComponentInspectorPanelComponent],
  template: `
    <div class="panel-header">
      <!-- ── Tabs (inside header so they collapse with minimized mode) ──── -->
      <div class="tab-group">
        <button class="tab-btn" [class.active]="activeTab() === 'console'" (click)="activeTab.set('console')">
          <mat-icon class="tab-icon">terminal</mat-icon> Consola
        </button>
        <button class="tab-btn" [class.active]="activeTab() === 'inspector'" (click)="activeTab.set('inspector')">
          <mat-icon class="tab-icon">account_tree</mat-icon> Inspector
        </button>
      </div>

      <!-- ── Tab-specific controls (right-aligned) ──────────────────────── -->
      @if (activeTab() === 'console') {
        <div class="filters">
          <div class="filter-dropdown" (click)="$event.stopPropagation()">
            <button class="filter-dropdown-toggle" (click)="filterDropdownOpen.set(!filterDropdownOpen())">
              <mat-icon class="filter-icon">filter_list</mat-icon>
              <span>Filtros</span>
              <span class="active-count">({{ enabledLevelCount() }}/{{ levels.length }})</span>
              <mat-icon class="chevron-icon">{{ filterDropdownOpen() ? 'expand_less' : 'expand_more' }}</mat-icon>
            </button>
            @if (filterDropdownOpen()) {
              <div class="filter-dropdown-menu">
                <label class="filter-option select-all" (click)="toggleAllLevels()">
                  <span class="custom-checkbox" [class.checked]="allLevelsEnabled()" [class.indeterminate]="someLevelsEnabled()">
                    @if (allLevelsEnabled()) { <mat-icon>check_box</mat-icon> }
                    @else if (someLevelsEnabled()) { <mat-icon>indeterminate_check_box</mat-icon> }
                    @else { <mat-icon>check_box_outline_blank</mat-icon> }
                  </span>
                  <span class="option-label">Select All</span>
                </label>
                <div class="filter-divider"></div>
                @for (level of levels; track level) {
                  <label class="filter-option" [class]="'option-' + level" (click)="toggleLevel(level)">
                    <span class="custom-checkbox" [class.checked]="isLevelEnabled(level)">
                      @if (isLevelEnabled(level)) { <mat-icon>check_box</mat-icon> }
                      @else { <mat-icon>check_box_outline_blank</mat-icon> }
                    </span>
                    <span class="chip-dot"></span>
                    <span class="option-label">{{ level.toUpperCase() }}</span>
                    <span class="chip-count">{{ countByLevel(level) }}</span>
                  </label>
                }
              </div>
            }
          </div>
        </div>

        <div class="header-actions">
          <input class="search-input" type="text" placeholder="Filter..."
                 [value]="searchQuery()"
                 (input)="searchQuery.set($any($event.target).value)" />
          <button class="header-btn" [class.active]="groupRepeated()" (click)="toggleGroupRepeated()"
                  title="Group repeated messages">
            <mat-icon>compress</mat-icon>
          </button>
          <button class="header-btn" [class.confirming-clear]="confirmingClear()"
                  (click)="onClearClick()"
                  [title]="confirmingClear() ? 'Click again to confirm' : 'Clear console'">
            @if (confirmingClear()) {
              <span class="confirm-text">Sure?</span>
            } @else {
              <mat-icon>delete_sweep</mat-icon>
            }
          </button>
        </div>
      }

      @if (activeTab() === 'inspector') {
        <div class="header-actions inspector-actions">
          <!-- Origin filter dropdown -->
          <div class="filter-dropdown" (click)="$event.stopPropagation()">
            <button class="filter-dropdown-toggle" (click)="originDropdownOpen.set(!originDropdownOpen())">
              <mat-icon class="filter-icon">filter_list</mat-icon>
              <span>Origen</span>
              <mat-icon class="chevron-icon">{{ originDropdownOpen() ? 'expand_less' : 'expand_more' }}</mat-icon>
            </button>
            @if (originDropdownOpen()) {
              <div class="filter-dropdown-menu origin-menu">
                @for (origin of inspectorAvailableOrigins(); track origin) {
                  <label class="filter-option" (click)="inspectorToggleOrigin(origin)">
                    <span class="custom-checkbox" [class.checked]="inspectorIsOriginEnabled(origin)">
                      @if (inspectorIsOriginEnabled(origin)) { <mat-icon>check_box</mat-icon> }
                      @else { <mat-icon>check_box_outline_blank</mat-icon> }
                    </span>
                    <span class="origin-badge" [class]="'origin-' + origin">{{ origin }}</span>
                  </label>
                }
              </div>
            }
          </div>

          <button class="header-btn" (click)="inspectorRescan()" title="Rescan">
            <mat-icon [class.spinning]="inspectorScanning()">refresh</mat-icon>
          </button>
          <button class="header-btn" (click)="inspectorExpandAll()" title="Expand all">
            <mat-icon>unfold_more</mat-icon>
          </button>
          <button class="header-btn" (click)="inspectorCollapseAll()" title="Collapse all">
            <mat-icon>unfold_less</mat-icon>
          </button>
          <button class="header-btn" (click)="inspectorSmartCollapse()" title="Smart collapse">
            <mat-icon>account_tree</mat-icon>
          </button>
          <span class="toolbar-sep"></span>
          <button class="header-btn" (click)="inspectorHighlightAll()" title="Resaltar todo">
            <mat-icon>visibility</mat-icon>
          </button>
          <button class="header-btn" (click)="inspectorHideAllHighlights()" title="Ocultar resaltados"
                  [class.active]="inspectorHasHighlights()">
            <mat-icon>visibility_off</mat-icon>
          </button>
          <span class="component-count">{{ inspectorComponentCount() }} componentes</span>
        </div>
      }
    </div>

    @if (activeTab() === 'console') {
      <div class="panel-body" #panelBody>
        @if (displayEntries().length === 0) {
          <div class="empty">No console output captured</div>
        }

        @if (pinnedEntries().length > 0) {
          <div class="pinned-header">
            <mat-icon class="pinned-header-icon">push_pin</mat-icon>
            Pinned
          </div>
          @for (entry of pinnedEntries(); track entry.id + '-' + (entry.repeatCount ?? 0)) {
            <ng-container *ngTemplateOutlet="consoleRow; context: { $implicit: entry }" />
          }
        }

        @for (entry of unpinnedEntries(); track entry.id + '-' + (entry.repeatCount ?? 0)) {
          <ng-container *ngTemplateOutlet="consoleRow; context: { $implicit: entry }" />
        }
      </div>
    }

    @if (activeTab() === 'inspector') {
      <app-component-inspector-panel [active]="activeTab() === 'inspector'" />
    }

    <ng-template #consoleRow let-entry>
      <div class="row" [class]="'level-' + entry.level" [class.pinned]="entry.pinned">
        <span class="cell time">{{ formatTime(entry.timestamp) }}</span>
        <span class="cell level-pill" [class]="'level-pill-' + entry.level">{{ entry.level.toUpperCase() }}</span>
        <span class="cell preview-cell">
          @if (entry.repeatCount && entry.repeatCount > 1) {
            <span class="repeat-badge">×{{ entry.repeatCount }}</span>
          }
          <span class="preview-text" (click)="toggleExpanded(entry.id)" [title]="previewTitle(entry)">
            {{ previewText(entry) }}
          </span>
        </span>
        <span class="cell source" [title]="entry.source || ''">{{ sourceShort(entry.source) }}</span>
        <span class="cell actions">
          <button class="icon-btn" (click)="toggleExpanded(entry.id)"
                  [title]="isExpanded(entry.id) ? 'Collapse' : 'Expand'">
            <mat-icon>{{ isExpanded(entry.id) ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
          <button class="icon-btn" (click)="togglePin(entry.id)" [title]="entry.pinned ? 'Unpin' : 'Pin'"
                  [class.pin-active]="entry.pinned">
            <mat-icon>{{ entry.pinned ? 'push_pin' : 'push_pin' }}</mat-icon>
          </button>
          <button class="icon-btn" (click)="copyEntry(entry)" title="Copy">
            <mat-icon>{{ copiedId() === entry.id ? 'check' : 'content_copy' }}</mat-icon>
          </button>
          <button class="icon-btn danger" [class.confirming]="confirmingDeleteId() === entry.id"
                  (click)="onDeleteClick(entry.id)"
                  [title]="confirmingDeleteId() === entry.id ? 'Click again to confirm' : 'Remove'">
            @if (confirmingDeleteId() === entry.id) {
              <span class="confirm-text">Sure?</span>
            } @else {
              <mat-icon>close</mat-icon>
            }
          </button>
        </span>
      </div>

      @if (isExpanded(entry.id)) {
        <div class="section-expanded">
          <div class="section-header">
            <span class="section-label">{{ entry.level.toUpperCase() }} — {{ formatTime(entry.timestamp) }}</span>
            <button class="icon-btn" (click)="copyEntry(entry)" title="Copy">
              <mat-icon>{{ copiedId() === entry.id ? 'check' : 'content_copy' }}</mat-icon>
            </button>
          </div>
          @for (arg of expandedArgs(entry); track $index) {
            <app-json-viewer [data]="viewableArg(arg)" mode="view" [resizable]="true" [initialHeight]="120" />
          }
          @if (entry.source) {
            <div class="source-detail">{{ entry.source }}</div>
          }
        </div>
      }
    </ng-template>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
      background: #14142b;
      color: #e5e7eb;
    }

    /* ── Header ───────────────────────────────────────────────────────────── */

    .panel-header {
      display: flex;
      align-items: center;
      background: var(--primary-dark-blue, #0d1b2a);
      border-bottom: 1px solid #23233a;
      flex-shrink: 0;
      padding: 4px 6px;
      gap: 6px;
    }

    /* ── Tab group (inline inside header) ─────────────────────────────── */

    .tab-group {
      display: flex;
      align-items: center;
      gap: 0;
      flex-shrink: 0;
    }

    .tab-btn {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      padding: 3px 10px;
      background: none;
      border: none;
      border-bottom: 2px solid transparent;
      color: #9ca3af;
      font-size: 0.66rem;
      font-weight: 600;
      font-family: ui-monospace, monospace;
      cursor: pointer;
      transition: color 0.15s, border-color 0.15s;
      white-space: nowrap;
    }
    .tab-btn:hover { color: #e5e7eb; }
    .tab-btn.active {
      color: #3b82f6;
      border-bottom-color: #3b82f6;
    }
    .tab-icon {
      font-size: 13px;
      width: 13px;
      height: 13px;
    }

    .filters {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-left: auto;
    }

    /* ── Filter dropdown ─────────────────────────────────────────────────── */

    .filter-dropdown {
      position: relative;
    }

    .filter-dropdown-toggle {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      background: #1f2937;
      border: 1px solid #23233a;
      border-radius: 4px;
      color: #e5e7eb;
      font-size: 0.68rem;
      font-weight: 600;
      font-family: ui-monospace, monospace;
      cursor: pointer;
      transition: background 0.12s, border-color 0.12s;
    }
    .filter-dropdown-toggle:hover { background: #374151; border-color: #3b82f6; }

    .filter-icon, .chevron-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
    }

    .active-count {
      color: #64748b;
      font-size: 0.62rem;
    }

    .filter-dropdown-menu {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      z-index: 100;
      min-width: 170px;
      background: #1a1a2e;
      border: 1px solid #3a3a5a;
      border-radius: 6px;
      padding: 4px 0;
      box-shadow: 0 8px 24px rgba(0,0,0,0.5);
      animation: dropdownIn 0.12s ease-out;
    }
    @keyframes dropdownIn {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .filter-option {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      cursor: pointer;
      font-size: 0.68rem;
      font-family: ui-monospace, monospace;
      color: #e5e7eb;
      transition: background 0.1s;
    }
    .filter-option:hover { background: #23233a; }

    .filter-option.select-all {
      font-weight: 700;
      color: #cbd5e1;
    }

    .filter-divider {
      height: 1px;
      background: #23233a;
      margin: 3px 0;
    }

    .custom-checkbox {
      display: inline-flex;
      align-items: center;
      color: #64748b;
    }
    .custom-checkbox.checked { color: #3b82f6; }
    .custom-checkbox.indeterminate { color: #f97316; }
    .custom-checkbox mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
    }

    .option-label { flex: 1; }

    .chip-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #6b7280;
      flex-shrink: 0;
    }
    .option-log   .chip-dot { background: #9ca3af; }
    .option-info  .chip-dot { background: #3b82f6; }
    .option-warn  .chip-dot { background: #f97316; }
    .option-error .chip-dot { background: #dc2626; }
    .option-debug .chip-dot { background: #a855f7; }

    .chip-count {
      color: #64748b;
      font-weight: 600;
      font-size: 0.62rem;
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 2px;
    }

    .search-input {
      background: #0f0f1e;
      border: 1px solid #3a3a5a;
      color: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 0.68rem;
      padding: 2px 6px;
      border-radius: 3px;
      outline: none;
      width: 100px;
    }
    .search-input:focus { border-color: #3b82f6; }

    .header-btn {
      background: none;
      border: none;
      color: #9ca3af;
      padding: 4px;
      cursor: pointer;
      border-radius: 3px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .header-btn:hover { background: #1f2937; color: #fff; }
    .header-btn mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
    }
    .header-btn.active { color: #3b82f6; }
    .header-btn.spinning, .spinning {
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }

    .inspector-actions {
      margin-left: auto;
    }

    .toolbar-sep {
      width: 1px;
      height: 14px;
      background: #3a3a5a;
      margin: 0 2px;
      flex-shrink: 0;
    }

    .component-count {
      font-size: 0.64rem;
      color: #64748b;
      font-family: ui-monospace, monospace;
      margin-left: 4px;
    }

    .origin-menu {
      right: 0;
      left: auto;
    }

    .origin-badge {
      font-size: 0.66rem;
      font-weight: 700;
      font-family: ui-monospace, monospace;
      padding: 1px 6px;
      border-radius: 3px;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
    .origin-app    { color: #34d399; }
    .origin-mat    { color: #818cf8; }
    .origin-ngx    { color: #fb923c; }
    .origin-cdk    { color: #38bdf8; }
    .origin-router { color: #f472b6; }
    .origin-forms  { color: #a78bfa; }
    .origin-other  { color: #6b7280; }
    .header-btn.confirming-clear {
      background: #991b1b;
      color: #fff;
      animation: confirmPulse 0.6s ease-in-out infinite alternate;
    }

    /* ── Body ─────────────────────────────────────────────────────────────── */

    .panel-body {
      flex: 1;
      overflow-y: scroll;
      overflow-x: hidden;
      scrollbar-gutter: stable;
    }
    .panel-body::-webkit-scrollbar { width: 10px; height: 10px; }
    .panel-body::-webkit-scrollbar-track { background: #0a0a18; }
    .panel-body::-webkit-scrollbar-thumb {
      background: #3a3a5a;
      border-radius: 5px;
      border: 2px solid #0a0a18;
    }
    .panel-body::-webkit-scrollbar-thumb:hover { background: #4a4a7a; }
    .panel-body {
      scrollbar-width: thin;
      scrollbar-color: #3a3a5a #0a0a18;
    }

    .empty {
      padding: 14px 12px;
      font-size: 0.74rem;
      color: #6b7280;
      text-align: center;
      font-style: italic;
    }

    .pinned-header {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 4px 10px;
      font-size: 0.68rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #fbbf24;
      background: rgba(251, 191, 36, 0.06);
      border-top: 1px solid rgba(251, 191, 36, 0.15);
      border-bottom: 1px solid rgba(251, 191, 36, 0.15);
      position: sticky;
      top: 0;
      z-index: 1;
    }
    .pinned-header-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
    }

    /* ── Row ──────────────────────────────────────────────────────────────── */

    .row {
      display: grid;
      grid-template-columns: 68px 54px minmax(0, 1fr) minmax(0, 160px) 90px;
      align-items: center;
      gap: 4px;
      padding: 2px 6px;
      font-size: 0.72rem;
      color: #d4d4d8;
      border-bottom: 1px solid #23233a;
      min-height: 24px;
      animation: rowSlideIn 0.35s ease-out;
    }
    @keyframes rowSlideIn {
      0% { opacity: 0; transform: translateX(-20px); }
      100% { opacity: 1; transform: translateX(0); }
    }
    .row:hover { background: #23233a; }

    .row.level-warn  { background: rgba(249, 115, 22, 0.06); }
    .row.level-error { background: rgba(220, 38, 38, 0.08); }
    .row.level-warn:hover  { background: rgba(249, 115, 22, 0.14); }
    .row.level-error:hover { background: rgba(220, 38, 38, 0.16); }
    .row.pinned { border-left: 2px solid #fbbf24; }

    .cell {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    .time {
      font-family: ui-monospace, monospace;
      font-size: 0.66rem;
      color: #94a3b8;
    }

    .level-pill {
      padding: 1px 5px;
      border-radius: 3px;
      text-align: center;
      font-weight: 700;
      font-size: 0.62rem;
      font-family: ui-monospace, monospace;
      color: #fff;
    }
    .level-pill-log   { background: #4b5563; }
    .level-pill-info  { background: #3b82f6; }
    .level-pill-warn  { background: #f97316; }
    .level-pill-error { background: #dc2626; }
    .level-pill-debug { background: #a855f7; }

    .preview-cell {
      display: flex;
      align-items: center;
      gap: 4px;
      min-width: 0;
      overflow: hidden;
    }

    .preview-text {
      font-family: ui-monospace, monospace;
      font-size: 0.7rem;
      color: #e5e7eb;
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }
    .preview-text:hover { color: #fff; text-decoration: underline; }

    .repeat-badge {
      background: #fbbf24;
      color: #1a1a2e;
      font-size: 0.62rem;
      font-weight: 800;
      padding: 1px 6px;
      border-radius: 8px;
      flex-shrink: 0;
      min-width: 28px;
      text-align: center;
    }

    .source {
      font-family: ui-monospace, monospace;
      font-size: 0.64rem;
      color: #64748b;
      text-align: right;
    }

    .actions {
      display: flex;
      gap: 1px;
      align-items: center;
      justify-content: flex-end;
    }

    .icon-btn {
      background: none;
      border: none;
      color: #9ca3af;
      padding: 2px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 3px;
      flex-shrink: 0;
    }
    .icon-btn mat-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
      line-height: 14px;
    }
    .icon-btn:hover:not(:disabled) { background: #374151; color: #fff; }
    .icon-btn.danger:hover:not(:disabled) { background: #991b1b; color: #fff; }
    .icon-btn.pin-active { color: #fbbf24; }
    .icon-btn.confirming {
      background: #991b1b;
      color: #fff;
      animation: confirmPulse 0.6s ease-in-out infinite alternate;
    }
    @keyframes confirmPulse {
      from { opacity: 1; }
      to { opacity: 0.6; }
    }
    .confirm-text {
      font-size: 0.58rem;
      font-weight: 700;
      letter-spacing: 0.02em;
    }

    /* ── Expanded section ─────────────────────────────────────────────────── */

    .section-expanded {
      background: #0a0a18;
      padding: 4px 6px 8px 6px;
      border-bottom: 1px solid #23233a;
    }
    .section-header {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 2px 4px;
    }
    .section-label {
      font-size: 0.66rem;
      font-weight: 600;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      flex: 1;
    }
    .section-expanded app-json-viewer {
      display: block;
      margin-bottom: 4px;
    }
    .source-detail {
      font-family: ui-monospace, monospace;
      font-size: 0.62rem;
      color: #64748b;
      padding: 4px 4px 0;
      word-break: break-all;
    }

  `],
})
export class DevConsolePanelComponent implements AfterViewInit {
  @ViewChild('panelBody') panelBodyRef?: ElementRef<HTMLElement>;
  @ViewChild(ComponentInspectorPanelComponent) inspectorPanel?: ComponentInspectorPanelComponent;
  private readonly service = inject(DevConsoleService);
  private readonly bottomBar = inject(DevBottomBarService);
  private readonly ngZone = inject(NgZone);
  private readonly isMinimized = toSignal(this.bottomBar.minimized$, { requireSync: true });

  readonly levels = CONSOLE_LEVELS;

  /** Active tab */
  readonly activeTab = signal<'console' | 'inspector'>('console');

  /** Filter dropdown open state */
  readonly filterDropdownOpen = signal(false);

  /** Origin dropdown open state (inspector tab) */
  readonly originDropdownOpen = signal(false);

  /** Text filter (#50) */
  readonly searchQuery = signal('');

  /** Group repeated messages (#51) — persisted in localStorage. */
  readonly groupRepeated = signal(localStorage.getItem('devBar.console.groupRepeated') === 'true');

  /** Expanded entry IDs (inline view instead of dialog) */
  readonly expandedIds = signal(new Set<string>());

  /** Clear confirmation (#53) */
  readonly confirmingClear = signal(false);
  private clearTimer: ReturnType<typeof setTimeout> | null = null;

  /** Delete confirmation (#61) */
  readonly confirmingDeleteId = signal<string | null>(null);
  private deleteTimer: ReturnType<typeof setTimeout> | null = null;

  /** Copy feedback (#63) */
  readonly copiedId = signal<string | null>(null);
  private copyTimer: ReturnType<typeof setTimeout> | null = null;

  /** Auto-scroll: only scroll to bottom when user is at the bottom edge. */
  private autoScrollEnabled = true;
  private lastEntryCount = 0;

  constructor() {
    // Force tab back to console when the bar is minimized.
    effect(() => {
      if (this.isMinimized()) {
        this.activeTab.set('console');
      }
    });

    // Deferred sync of inspector component count to avoid NG0100.
    // The child's totalCount() can change mid-CD (via MutationObserver scans),
    // so we push it into a local signal outside the current CD cycle.
    effect(() => {
      const count = this.inspectorPanel?.totalCount() ?? 0;
      setTimeout(() => this.inspectorComponentCount.set(count));
    });

    effect(() => {
      const count = this.service.entries().length;
      if (count > this.lastEntryCount && this.autoScrollEnabled) {
        this.lastEntryCount = count;
        // Run outside zone so this setTimeout doesn't trigger a CD cycle
        // that would re-check TransportQuotesComponent and cause NG0100 loops.
        this.ngZone.runOutsideAngular(() => setTimeout(() => this.scrollToBottom()));
      } else {
        this.lastEntryCount = count;
      }
    });
  }

  ngAfterViewInit(): void {
    const el = this.panelBodyRef?.nativeElement;
    if (el) {
      el.addEventListener('scroll', () => {
        const threshold = 40;
        this.autoScrollEnabled = (el.scrollTop + el.clientHeight) >= (el.scrollHeight - threshold);
      });
    }
  }

  private scrollToBottom(): void {
    const el = this.panelBodyRef?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }

  /** Filtered, grouped, and sorted entries. */
  readonly displayEntries = computed(() => {
    const enabled = this.service.enabledLevels();
    const query = this.searchQuery().toLowerCase().trim();
    let entries = this.service.entries().filter(e => {
      if (!enabled.has(e.level)) return false;
      if (query) {
        const text = e.args.map(a => this.previewArg(a)).join(' ').toLowerCase();
        if (!text.includes(query) && !(e.source?.toLowerCase().includes(query))) return false;
      }
      return true;
    });

    // Group repeated consecutive messages (#51)
    if (this.groupRepeated()) {
      entries = this.groupConsecutive(entries);
    }

    return entries;
  });

  /** Pinned entries separated from the rest. */
  readonly pinnedEntries = computed(() => this.displayEntries().filter(e => e.pinned));
  readonly unpinnedEntries = computed(() => this.displayEntries().filter(e => !e.pinned));

  isLevelEnabled(level: ConsoleLevel): boolean {
    return this.service.enabledLevels().has(level);
  }

  countByLevel(level: ConsoleLevel): number {
    return this.service.entries().filter(e => e.level === level).length;
  }

  toggleLevel(level: ConsoleLevel): void {
    this.service.toggleLevel(level);
  }

  readonly enabledLevelCount = computed(() => this.service.enabledLevels().size);
  readonly allLevelsEnabled = computed(() => this.service.enabledLevels().size === CONSOLE_LEVELS.length);
  readonly someLevelsEnabled = computed(() => {
    const size = this.service.enabledLevels().size;
    return size > 0 && size < CONSOLE_LEVELS.length;
  });

  toggleAllLevels(): void {
    this.service.setAllLevels(!this.allLevelsEnabled());
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.filterDropdownOpen()) this.filterDropdownOpen.set(false);
    if (this.originDropdownOpen()) this.originDropdownOpen.set(false);
  }

  // ── Inspector toolbar delegates ───────────────────────────────────────────

  inspectorRescan(): void { this.inspectorPanel?.service.scan(); }
  inspectorExpandAll(): void { this.inspectorPanel?.expandAll(); }
  inspectorCollapseAll(): void { this.inspectorPanel?.collapseAll(); }
  inspectorSmartCollapse(): void { this.inspectorPanel?.smartCollapse(); }
  inspectorScanning(): boolean { return this.inspectorPanel?.service.scanning() ?? false; }
  readonly inspectorComponentCount = signal(0);
  inspectorAvailableOrigins(): string[] { return this.inspectorPanel?.availableOrigins() ?? []; }
  inspectorToggleOrigin(origin: string): void { this.inspectorPanel?.toggleOrigin(origin); }
  inspectorIsOriginEnabled(origin: string): boolean { return this.inspectorPanel?.isOriginEnabled(origin) ?? false; }
  inspectorHighlightAll(): void { this.inspectorPanel?.highlightAll(); }
  inspectorHideAllHighlights(): void { this.inspectorPanel?.hideAllHighlights(); }
  inspectorHasHighlights(): boolean { return this.inspectorPanel?.hasHighlights() ?? false; }

  toggleGroupRepeated(): void {
    const next = !this.groupRepeated();
    this.groupRepeated.set(next);
    try { localStorage.setItem('devBar.console.groupRepeated', String(next)); } catch {/* quota */}
  }

  // ── Clear with confirmation (#53) ─────────────────────────────────────────

  onClearClick(): void {
    if (this.confirmingClear()) {
      if (this.clearTimer) clearTimeout(this.clearTimer);
      this.confirmingClear.set(false);
      this.service.clear();
    } else {
      this.confirmingClear.set(true);
      this.clearTimer = setTimeout(() => this.confirmingClear.set(false), 2000);
    }
  }

  // ── Inline delete confirmation (#61) ──────────────────────────────────────

  onDeleteClick(id: string): void {
    if (this.confirmingDeleteId() === id) {
      if (this.deleteTimer) clearTimeout(this.deleteTimer);
      this.confirmingDeleteId.set(null);
      this.service.remove(id);
    } else {
      this.confirmingDeleteId.set(id);
      if (this.deleteTimer) clearTimeout(this.deleteTimer);
      this.deleteTimer = setTimeout(() => this.confirmingDeleteId.set(null), 2000);
    }
  }

  // ── Pin/highlight (#52) ───────────────────────────────────────────────────

  togglePin(id: string): void {
    this.service.togglePin(id);
  }

  // ── Expanded inline view ──────────────────────────────────────────────────

  toggleExpanded(id: string): void {
    this.expandedIds.update(set => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  isExpanded(id: string): boolean {
    return this.expandedIds().has(id);
  }

  // ── Copy with feedback (#63) ──────────────────────────────────────────────

  copyEntry(entry: ConsoleEntry): void {
    const text = entry.args.map(a => this.stringifyArg(a)).join('\n\n');
    navigator.clipboard.writeText(text);
    if (this.copyTimer) clearTimeout(this.copyTimer);
    this.copiedId.set(entry.id);
    this.copyTimer = setTimeout(() => this.copiedId.set(null), 1500);
  }

  // ── Row rendering helpers ─────────────────────────────────────────────────

  formatTime(ts: number): string {
    const d = new Date(ts);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    const ms = String(d.getMilliseconds()).padStart(3, '0');
    return `${hh}:${mm}:${ss}.${ms}`;
  }

  previewText(entry: ConsoleEntry): string {
    const full = entry.args.map(a => this.previewArg(a)).join(' ');
    return full.length > 200 ? full.slice(0, 200) + '…' : full;
  }

  previewTitle(entry: ConsoleEntry): string {
    return entry.args.map(a => this.stringifyArg(a)).join(' ');
  }

  sourceShort(source: string | undefined): string {
    if (!source) return '';
    // Service formats as "ClassName.method (file:line:col)" — show fn + line.
    const withFn = source.match(/^(.+?)\s+\([^:]*:(\d+)(?::\d+)?\)$/);
    if (withFn) return `${withFn[1]}:${withFn[2]}`;
    const locOnly = source.match(/([^/\\() ]+:\d+(:\d+)?)\)?$/);
    return locOnly ? locOnly[1] : source;
  }

  // ── Group consecutive duplicates (#51) ────────────────────────────────────

  private groupConsecutive(entries: ConsoleEntry[]): ConsoleEntry[] {
    if (entries.length === 0) return entries;
    const result: ConsoleEntry[] = [];
    let current = { ...entries[0], repeatCount: 1 };

    for (let i = 1; i < entries.length; i++) {
      const e = entries[i];
      if (e.level === current.level && this.argsEqual(e.args, current.args)) {
        current.repeatCount!++;
      } else {
        result.push(current);
        current = { ...e, repeatCount: 1 };
      }
    }
    result.push(current);
    return result;
  }

  private argsEqual(a: unknown[], b: unknown[]): boolean {
    if (a.length !== b.length) return false;
    try {
      const replacer = (_key: string, value: unknown) => _key === 'stack' ? undefined : value;
      return JSON.stringify(a, replacer) === JSON.stringify(b, replacer);
    } catch {
      return false;
    }
  }

  /** Filter args for expanded view: drop bare string labels when a serialized error object is present. */
  expandedArgs(entry: ConsoleEntry): unknown[] {
    const hasErrorObj = entry.args.some(a =>
      a !== null && typeof a === 'object' && '_type' in (a as Record<string, unknown>)
    );
    if (!hasErrorObj) return entry.args;
    return entry.args.filter(a => typeof a !== 'string');
  }

  /** Convert Error/exception objects to a plain object so JsonViewer can display them. */
  viewableArg(arg: unknown): unknown {
    if (arg instanceof Error) return this.errorToObject(arg);
    if (arg && typeof arg === 'object' && 'error' in arg && 'status' in arg) {
      // HttpErrorResponse-like
      const obj = arg as Record<string, unknown>;
      return {
        name: obj['name'] ?? 'HttpErrorResponse',
        status: obj['status'],
        statusText: obj['statusText'],
        url: obj['url'],
        message: obj['message'],
        error: obj['error'],
        ...(obj['headers'] ? { headers: obj['headers'] } : {}),
      };
    }
    return arg;
  }

  private errorToObject(err: Error): Record<string, unknown> {
    const obj: Record<string, unknown> = {
      name: err.name,
      message: err.message,
    };
    // Copy own enumerable properties (Angular's HttpErrorResponse has many)
    for (const key of Object.keys(err)) {
      if (key !== 'name' && key !== 'message' && key !== 'stack') {
        obj[key] = (err as any)[key];
      }
    }
    if (err.stack) obj['stack'] = err.stack;
    if (err.cause) {
      obj['cause'] = err.cause instanceof Error ? this.errorToObject(err.cause) : err.cause;
    }
    return obj;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private previewArg(v: unknown): string {
    if (v === null) return 'null';
    if (v === undefined) return 'undefined';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
    if (v instanceof Error) {
      return `${v.name}: ${v.message}`;
    }
    // Serialized error/HttpErrorResponse from capture time
    if (typeof v === 'object' && v !== null && '_type' in v && 'message' in v) {
      const obj = v as Record<string, unknown>;
      return `${obj['_type']}: ${obj['message']}`;
    }
    try {
      return JSON.stringify(v, this.safeReplacer());
    } catch {
      return String(v);
    }
  }

  private stringifyArg(v: unknown): string {
    if (v === null) return 'null';
    if (v === undefined) return 'undefined';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
    if (v instanceof Error) return this.stringifyError(v);
    try {
      return JSON.stringify(v, this.safeReplacer(), 2);
    } catch {
      return String(v);
    }
  }

  private stringifyError(err: Error): string {
    const parts: string[] = [`${err.name}: ${err.message}`];
    for (const key of Object.keys(err)) {
      if (key === 'message' || key === 'name') continue;
      try {
        const val = (err as any)[key];
        parts.push(`  ${key}: ${JSON.stringify(val, this.safeReplacer())}`);
      } catch {
        parts.push(`  ${key}: [unserializable]`);
      }
    }
    if (err.stack) parts.push(err.stack);
    if (err.cause) {
      parts.push(`Cause: ${err.cause instanceof Error ? this.stringifyError(err.cause) : String(err.cause)}`);
    }
    return parts.join('\n');
  }

  private safeReplacer(): (key: string, value: unknown) => unknown {
    const seen = new WeakSet();
    return (_key: string, value: unknown) => {
      if (value instanceof Error) {
        const obj: Record<string, unknown> = {
          name: value.name,
          message: value.message,
          stack: value.stack,
        };
        for (const k of Object.keys(value)) {
          obj[k] = (value as any)[k];
        }
        if (value.cause) obj['cause'] = value.cause;
        return obj;
      }
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return '[Circular]';
        seen.add(value);
      }
      return value;
    };
  }
}
